import { useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { useErrorHandler } from "../../../../../_metronic/helpers/ErrorHandler";
import { openAlert } from "../../../../../_metronic/elements/NotificationAlert";
import { ConfirmPassword, Email, FirstName, LastName, Password, Phone } from "./Input";
import Action from "./Action";
import * as AuthService from '../../services/AuthService';

const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const REGISTRATION_UTM_STORAGE_KEY = "specific_registration_utm_first_touch";
const REGISTRATION_UTM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const getRegistrationUtm = (search = window.location.search) => {
  const params = new URLSearchParams(search || "");
  return UTM_FIELDS.reduce((utm, field) => {
    const value = (params.get(field) || "").trim();
    if (value) utm[field] = value.slice(0, 256);
    return utm;
  }, {});
};

export const getFirstTouchRegistrationUtm = (
  search = window.location.search,
  storage = window.localStorage,
  now = Date.now()
) => {
  const current = getRegistrationUtm(search);
  try {
    const payload = JSON.parse(
      storage.getItem(REGISTRATION_UTM_STORAGE_KEY) || "{}"
    );
    const expired = Number.isFinite(Number(payload.captured_at))
      && now - Number(payload.captured_at) > REGISTRATION_UTM_MAX_AGE_MS;
    if (expired) storage.removeItem(REGISTRATION_UTM_STORAGE_KEY);
    const storedPayload = expired ? {} : (payload.utm || payload);
    const stored = getRegistrationUtm(
      new URLSearchParams(storedPayload).toString()
    );
    if (Object.keys(stored).length) return stored;
    if (Object.keys(current).length) {
      storage.setItem(REGISTRATION_UTM_STORAGE_KEY, JSON.stringify({
        utm: current,
        captured_at: now,
      }));
    }
  } catch (_) {
    // Registration must remain available when browser storage is unavailable.
  }
  return current;
};

export const clearFirstTouchRegistrationUtm = (
  storage = window.localStorage
) => {
  try {
    storage.removeItem(REGISTRATION_UTM_STORAGE_KEY);
  } catch (_) {
    // A successful registration must not depend on browser storage.
  }
};

const Registration = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { errorHandler } = useErrorHandler();
  const navigate = useNavigate();
  const [registrationUtm] = useState(() => getFirstTouchRegistrationUtm());

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      repeatpassword: ""
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string()
        .min(3, t("auth:create_account.input.first_name.errors.min"))
        .max(50, t("auth:create_account.input.first_name.errors.max"))
        .required(t("auth:create_account.input.first_name.errors.required")),
      last_name: Yup.string()
        .min(3, t("auth:create_account.input.last_name.errors.min"))
        .max(50, t("auth:create_account.input.last_name.errors.max"))
        .required(t("auth:create_account.input.last_name.errors.required")),
      email: Yup.string()
        .email(t("auth:create_account.input.email.errors.email"))
        .max(50, t("auth:create_account.input.email.errors.max"))
        .required(t("auth:create_account.input.email.errors.required")),
      phone: Yup.string()
        .max(64, t("auth:create_account.input.phone.errors.max")),
      password: Yup.string()
        .min(8, t("auth:create_account.input.password.errors.min"))
        .max(32, t("auth:create_account.input.password.errors.max"))
        .required(t("auth:create_account.input.password.errors.required")),
      repeatpassword: Yup.string()
        .required(t("auth:create_account.input.repeatpassword.errors.required"))
        .when("password", {
          is: (val) => (val && val.length > 0 ? true : false),
          then: Yup.string().oneOf(
            [Yup.ref("password")],
            t("auth:create_account.input.repeatpassword.errors.equal")
          ),
        })
    }),
    onSubmit: (values, { resetForm }) => {
      enableLoading();

      const body = {
        email: values.email,
        phone: values.phone.trim() || undefined,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
        utm: registrationUtm,
      };

      errorHandler(AuthService.register(body))
        .then((res) => {
          if (!res.ok) {
            disableLoading();
            return;
          }

          openAlert('success', t("auth:create_account.success.title"), t("auth:create_account.success.message"));
          clearFirstTouchRegistrationUtm();
          disableLoading();
          navigate('/auth/login');
          resetForm();
        });
    },
  });

  return (
    <div className="login-form login-signin" style={{ display: "block" }}>
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          {t("auth:create_account.title")}
        </h3>
      </div>
      <form
        className="form fv-plugins-bootstrap fv-plugins-framework"
        onSubmit={formik.handleSubmit}
      >
        <Email loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <FirstName loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <LastName loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <Phone loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <Password loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <ConfirmPassword loading={loading} formik={formik} getInputClasses={getInputClasses} />
        <Action formik={formik} loading={loading} />
      </form>
    </div>
  );
}

export default Registration;
